'use client'
import type {
  RegisterFormValues,
} from './constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'

import { useForm, useWatch } from 'react-hook-form'
import { Link, useRouter } from '~/i18n/routing'
import { getQueryClient, TAG_TYPES } from '~/lib/config'
import useRegister from '~/lib/hooks/auth/use-register'
import { PasswordInput } from '~/modules/common/components/password-input'
import { Button } from '~/modules/common/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '~/modules/common/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/modules/common/components/ui/form'
import { Input } from '~/modules/common/components/ui/input'

import { toast } from '~/modules/common/components/ui/use-toast'
import { TypographyH1 } from '../../common/components/typography/typography'
import {
  getInitialDefaultValues,
  getRegisterFormSchema,
} from './constants'

export function RegisterForm({
  invitationToken,
}: {
  invitationToken?: string
}) {
  const t = useTranslations('page.register.registerForm')

  const router = useRouter()
  const { mutateAsync, isPending } = useRegister()
  const registerFormSubmitRequest = async (data: RegisterFormValues) => {
    return await mutateAsync({ ...data })
  }
  const defaultValues = getInitialDefaultValues({})
  const queryClient = getQueryClient()
  const loginFormSchema = getRegisterFormSchema({ t })
  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })
  const password = useWatch({ control: form.control, name: 'password' })

  async function onSubmit(data: RegisterFormValues) {
    try {
      const user = await registerFormSubmitRequest(data)
      if ('data' in user) {
        queryClient.setQueryData([TAG_TYPES.AUTH], user)
        router.push(
          `/verify${invitationToken ? `?invitationToken=${invitationToken}` : ''}`,
        )
      }
    }
    catch (err) {
      const { error } = err as APIError
      if (error?.code !== 500) {
        toast({
          variant: 'destructive',
          title: error.message,
        })
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
        <Card className="mx-auto max-w-md p-4">
          <CardHeader>
            <TypographyH1>{t('title')}</TypographyH1>
            <CardDescription>{t('description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="userName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          required
                          {...field}
                          placeholder={t('inputs.username.placeholder')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('inputs.email.label')}</FormLabel>
                      <FormControl>
                        <Input
                          id="email"
                          placeholder="m@example.com"
                          type="email"
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <PasswordInput
                      field={field}
                      showChecklist
                      password={password}
                      label={t('inputs.password.label')}
                    />
                  )}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isPending}
                isLoading={isPending}
              >
                {t('createAccount')}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              {t('alreadyHaveAccount')}
              {' '}
              <Link href="/login" className="underline">
                {t('signIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  )
}
